import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import { getCityBySlug, cities } from "@/mocks/cityData";

export default function CityPage() {
  const { citySlug } = useParams<{ citySlug: string }>();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const city = getCityBySlug(citySlug ?? "");

  useEffect(() => {
    if (!city) return;
    document.title = city.metaTitle;
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", city.metaDescription);
    setMeta("keywords", city.metaKeywords);
    setMeta("geo.position", `${city.geoLat};${city.geoLon}`);
    setMeta("geo.region", city.geoRegion);
    setMeta("geo.placename", `${city.city}, ${city.stateCode}`);
    setMeta("og:title", city.metaTitle, true);
    setMeta("og:description", city.metaDescription, true);
    setMeta("og:type", "website", true);
    let canonical = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://treetrimmingprices.com/tree-service/${city.slug}`);
  }, [city]);

  if (!city) {
    return (
      <div className="min-h-screen flex flex-col">
        <SiteNavbar />
        <main className="flex-1 flex flex-col items-center justify-center gap-4 py-32">
          <div className="w-16 h-16 flex items-center justify-center bg-[#E8F5E9] rounded-full">
            <i className="ri-map-pin-line text-[#1B4332] text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-[#1B4332]">City Not Found</h1>
          <p className="text-gray-500 text-sm">We don&apos;t have a page for that city yet.</p>
          <button
            onClick={() => navigate("/service-areas")}
            className="mt-2 px-6 py-2.5 bg-[#1B4332] text-white font-semibold text-sm rounded-lg cursor-pointer whitespace-nowrap"
          >
            View All Service Areas
          </button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const otherCities = cities.filter((c) => c.slug !== city.slug).slice(0, 6);

  return (
    <div className="min-h-screen font-sans bg-white">
      <SiteNavbar />

      {/* Hero */}
      <section
        className="relative w-full min-h-[420px] md:min-h-[500px] flex items-end overflow-hidden"
        style={{
          backgroundImage: `url('${city.heroImage}')`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/20"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pb-12 pt-32 md:pt-40">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-white/60 text-xs mb-4 flex-wrap">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} className="hover:text-white cursor-pointer">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <a href="/service-areas" onClick={(e) => { e.preventDefault(); navigate("/service-areas"); }} className="hover:text-white cursor-pointer">Service Areas</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/90">{city.city}, {city.stateCode}</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-3 py-1 mb-3">
                <i className="ri-map-pin-line text-green-400 text-xs"></i>
                <span className="text-white text-xs font-medium">{city.region} · {city.stateCode}</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-2">
                {city.heroHeadline}
              </h1>
              <p className="text-white/80 text-base md:text-lg max-w-2xl">{city.heroSubline}</p>
            </div>
            <button
              onClick={() => navigate(`/free-quote?city=${city.city}`)}
              className="flex-shrink-0 flex items-center gap-2 bg-[#1B4332] hover:bg-[#2d6a4f] text-white font-bold text-sm px-7 py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Free Quote in {city.city} <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Quick Price Bar */}
      <section className="bg-[#1B4332] py-5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-0 md:divide-x divide-white/20">
            {[
              { label: "Tree Trimming", price: city.avgTrim, icon: "ri-scissors-cut-line" },
              { label: "Tree Removal", price: city.avgRemoval, icon: "ri-delete-bin-line" },
              { label: "Stump Grinding", price: city.avgStump, icon: "ri-tools-line" },
              { label: "Emergency Service", price: city.avgEmergency, icon: "ri-alarm-warning-line" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 md:px-8 first:md:pl-0 last:md:pr-0">
                <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-green-400 text-sm`}></i>
                </div>
                <div>
                  <p className="text-white/60 text-xs">{item.label}</p>
                  <p className="text-white font-bold text-sm">{item.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* Main Content */}
          <div className="lg:col-span-2 flex flex-col gap-14">

            {/* Intro */}
            <section>
              <h2 className="text-2xl font-extrabold text-[#1B4332] mb-4">
                Tree Service in {city.city}, {city.stateCode}
              </h2>
              <p className="text-gray-600 leading-relaxed text-base">{city.intro}</p>
            </section>

            {/* Pricing Table */}
            <section>
              <h2 className="text-2xl font-extrabold text-[#1B4332] mb-6">
                Tree Trimming Cost in {city.city}
              </h2>
              <div className="rounded-2xl border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1B4332] text-white">
                      <th className="text-left px-5 py-3.5 font-semibold">Tree Size</th>
                      <th className="text-left px-5 py-3.5 font-semibold">Height</th>
                      <th className="text-right px-5 py-3.5 font-semibold">Avg. Trimming Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { size: "Small Tree", height: "Under 30 ft", price: city.smallTree },
                      { size: "Medium Tree", height: "30–60 ft", price: city.mediumTree },
                      { size: "Large Tree", height: "60+ ft", price: city.largeTree },
                    ].map((row, i) => (
                      <tr key={row.size} className={i % 2 === 0 ? "bg-white" : "bg-[#F7F9F8]"}>
                        <td className="px-5 py-3.5 font-semibold text-[#1B4332]">{row.size}</td>
                        <td className="px-5 py-3.5 text-gray-500">{row.height}</td>
                        <td className="px-5 py-3.5 text-right font-bold text-[#1B4332]">{row.price}</td>
                      </tr>
                    ))}
                    <tr className="bg-[#E8F5E9]">
                      <td className="px-5 py-3.5 font-semibold text-[#1B4332]">Stump Grinding</td>
                      <td className="px-5 py-3.5 text-gray-500">Any size</td>
                      <td className="px-5 py-3.5 text-right font-bold text-[#1B4332]">{city.avgStump}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-5 py-3.5 font-semibold text-[#1B4332]">Emergency Removal</td>
                      <td className="px-5 py-3.5 text-gray-500">Any size</td>
                      <td className="px-5 py-3.5 text-right font-bold text-[#1B4332]">{city.avgEmergency}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                * Prices are estimates based on local market data. Get a free quote for your exact cost.
              </p>
            </section>

            {/* Local Factors */}
            <section>
              <h2 className="text-2xl font-extrabold text-[#1B4332] mb-5">
                What Affects Tree Service Prices in {city.city}?
              </h2>
              <div className="flex flex-col gap-3">
                {city.localFactors.map((factor, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#F7F9F8] rounded-xl px-5 py-4">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#1B4332] text-base"></i>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">{factor}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Local Pros */}
            <section>
              <h2 className="text-2xl font-extrabold text-[#1B4332] mb-6">
                Top Tree Service Pros in {city.city}
              </h2>
              <div className="flex flex-col gap-4">
                {city.pros.map((pro) => (
                  <div key={pro.name} className="bg-white border border-gray-100 rounded-2xl p-5 flex flex-col sm:flex-row gap-4 hover:border-[#1B4332]/30 transition-colors">
                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-50 border border-gray-100">
                      <img src={pro.avatar} alt={pro.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-bold text-[#1B4332] text-base">{pro.name}</h3>
                        <span className="text-xs font-semibold bg-[#E8F5E9] text-[#1B4332] px-2.5 py-0.5 rounded-full whitespace-nowrap">{pro.badge}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className={`text-xs ${i < Math.floor(pro.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"}`}></i>
                          ))}
                        </div>
                        <span className="text-sm font-bold text-gray-800">{pro.rating}</span>
                        <span className="text-sm text-gray-400">({pro.reviews} reviews)</span>
                        <span className="text-gray-300">·</span>
                        <span className="text-sm text-gray-500">{pro.years} yrs</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{pro.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {pro.services.map((s) => (
                          <span key={s} className="text-xs bg-[#E8F5E9] text-[#1B4332] px-2.5 py-1 rounded-full font-medium whitespace-nowrap">{s}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col items-start sm:items-end justify-between gap-2 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Responds in {pro.responseTime}</p>
                        <p className="text-sm font-bold text-[#1B4332] mt-0.5">{pro.price}</p>
                      </div>
                      <button
                        onClick={() => navigate(`/free-quote?city=${city.city}`)}
                        className="px-5 py-2.5 bg-[#1B4332] hover:bg-[#2d6a4f] text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Request Quote
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Common Trees */}
            <section>
              <h2 className="text-2xl font-extrabold text-[#1B4332] mb-4">
                Common Trees in {city.city}
              </h2>
              <p className="text-gray-600 text-sm mb-5">
                These are the most common tree species our pros service in the {city.city} area. Each species has unique care requirements and pricing.
              </p>
              <div className="flex flex-wrap gap-3">
                {city.commonTrees.map((tree) => (
                  <div key={tree} className="flex items-center gap-2 bg-[#F7F9F8] border border-gray-100 rounded-xl px-4 py-2.5">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-tree-line text-[#1B4332] text-sm"></i>
                    </div>
                    <span className="text-sm font-medium text-[#1B4332]">{tree}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section>
              <h2 className="text-2xl font-extrabold text-[#1B4332] mb-6">
                {city.city} Tree Service FAQs
              </h2>
              <div className="flex flex-col gap-3">
                {city.faqs.map((faq, idx) => (
                  <div key={idx} className="border border-gray-100 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer hover:bg-[#F7F9F8] transition-colors"
                    >
                      <span className="font-semibold text-[#1B4332] text-sm md:text-base">{faq.q}</span>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`text-[#1B4332] text-lg transition-transform duration-200 ${openFaq === idx ? "ri-subtract-line" : "ri-add-line"}`}></i>
                      </div>
                    </button>
                    {openFaq === idx && (
                      <div className="px-5 pb-5">
                        <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1 flex flex-col gap-6">

            {/* CTA Card */}
            <div className="bg-[#1B4332] rounded-2xl p-6 text-white sticky top-20">
              <div className="w-12 h-12 flex items-center justify-center bg-white/15 rounded-xl mb-4">
                <i className="ri-file-list-3-line text-green-400 text-xl"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Get Your Free Quote</h3>
              <p className="text-white/70 text-sm mb-5 leading-relaxed">
                Connect with licensed {city.city} tree pros. No obligation, no pressure — just honest pricing.
              </p>
              <div className="flex flex-col gap-3 mb-5">
                {[
                  "Licensed & insured pros",
                  "Free on-site estimates",
                  "Responds within 24 hours",
                  "No hidden fees",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-white/80">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-green-400 text-sm"></i>
                    </div>
                    {item}
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate(`/free-quote?city=${city.city}`)}
                className="w-full py-3 bg-white text-[#1B4332] font-bold text-sm rounded-xl hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Get Free Quote in {city.city}
              </button>
            </div>

            {/* City Image */}
            <div className="rounded-2xl overflow-hidden w-full h-52">
              <img
                src={city.cityImage}
                alt={`Tree service in ${city.city}`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Trust Badges */}
            <div className="bg-[#F7F9F8] rounded-2xl p-5">
              <p className="text-xs font-bold text-[#1B4332] uppercase tracking-widest mb-4">Why Use Our Service</p>
              <div className="flex flex-col gap-3">
                {[
                  { icon: "ri-shield-check-line", label: "Licensed Professionals", desc: "All pros are verified" },
                  { icon: "ri-secure-payment-line", label: "Fully Insured", desc: "Liability + workers comp" },
                  { icon: "ri-star-line", label: "Verified Reviews", desc: "Real homeowner ratings" },
                  { icon: "ri-time-line", label: "Fast Response", desc: "Quotes within 24 hours" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#E8F5E9] rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-[#1B4332] text-base`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1B4332]">{item.label}</p>
                      <p className="text-xs text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Other Cities */}
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <p className="text-xs font-bold text-[#1B4332] uppercase tracking-widest mb-4">Other Cities We Serve</p>
              <div className="flex flex-col gap-2">
                {otherCities.map((c) => (
                  <a
                    key={c.slug}
                    href={`/tree-service/${c.slug}`}
                    onClick={(e) => { e.preventDefault(); navigate(`/tree-service/${c.slug}`); }}
                    className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 cursor-pointer group"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-line text-[#1B4332] text-xs"></i>
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-[#1B4332] group-hover:font-semibold transition-all">
                        {c.city}, {c.stateCode}
                      </span>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#1B4332] transition-colors"></i>
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Bottom CTA */}
      <section className="bg-[#F7F9F8] border-t border-gray-100 py-14">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#1B4332] mb-3">
            Ready to Get Your Free Quote in {city.city}?
          </h2>
          <p className="text-gray-500 text-base mb-7">
            Connect with licensed, insured tree service professionals in {city.city}, {city.stateCode}. No obligation, no pressure.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate(`/free-quote?city=${city.city}`)}
              className="px-8 py-3.5 bg-[#1B4332] hover:bg-[#2d6a4f] text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Free Quote <i className="ri-arrow-right-line ml-1"></i>
            </button>
            <button
              onClick={() => navigate("/cost-guide")}
              className="px-8 py-3.5 border-2 border-[#1B4332] text-[#1B4332] font-bold text-sm rounded-xl hover:bg-[#1B4332] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              View Pricing Guide
            </button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
